import React, { useState, useEffect } from 'react';
import { Plus, Trash2, MapPin, Edit, Mail, ChevronLeft, ChevronRight, Info } from 'lucide-react'; 
import Header from '../components/common/Header';
import UserSidebar from '../components/common/UserSidebar';
import { customerService } from '../services/customerService'; 
import { toast } from 'react-toastify';

const UserAddressPage = () => {
    const userStr = localStorage.getItem('user_info');
    const user = userStr ? JSON.parse(userStr) : {};
    
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const emptyForm = { receiverName: '', receiverPhone: '', receiverEmail: '', province: '', ward: '', specificAddress: '', isDefault: false };
    const [formData, setFormData] = useState(emptyForm);

    const fetchAddresses = async () => {
        try {
            const res = await customerService.getAddresses();
            setAddresses(res.data || res || []);
        } catch (error) { 
            console.error("Lỗi lấy địa chỉ", error); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchAddresses(); }, []);

    const handleEditClick = (addr) => {
        setFormData({
            receiverName: addr.receiverName,
            receiverPhone: addr.receiverPhone,
            receiverEmail: addr.receiverEmail || '',
            province: addr.province,
            ward: addr.ward || '',
            specificAddress: addr.specificAddress,
            isDefault: addr.isDefault || false
        });
        setEditingId(addr.addressId);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData(emptyForm);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await customerService.updateAddress(editingId, formData);
                toast.success("Cập nhật địa chỉ thành công!");
            } else {
                await customerService.createAddress(formData);
                toast.success("Thêm địa chỉ thành công!");
                setCurrentPage(1); // Reset page về 1 nếu thêm địa chỉ mới
            }
            handleCancel();
            fetchAddresses();
        } catch (error) { 
            toast.error(editingId ? "Lỗi khi cập nhật!" : "Lỗi khi thêm địa chỉ!"); 
        }
    };

    const handleDelete = async (id) => {
        if(window.confirm("Xóa địa chỉ này khỏi Sổ địa chỉ?")) {
            try {
                await customerService.deleteAddress(id);
                toast.success("Xóa thành công!");
                fetchAddresses();
            } catch (error) { toast.error("Lỗi khi xóa!"); }
        }
    };

    // Pagination Logic
    const totalPages = Math.ceil(addresses.length / itemsPerPage) || 1;
    const validCurrentPage = Math.min(currentPage, totalPages);
    const paginatedAddresses = addresses.slice((validCurrentPage - 1) * itemsPerPage, validCurrentPage * itemsPerPage);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            <main className="container mx-auto px-4 py-8 flex-grow">
                <div className="flex flex-col md:flex-row gap-6">
                    <UserSidebar user={user} />
                    <div className="flex-1 bg-gradient-to-r from-orange-50 to-white rounded-xl shadow-sm border border-orange-200/60 p-6 md:p-8">
                        <div className="flex justify-between items-center mb-6 pb-4 border-b border-orange-200/60">
                            <div>
                                <h2 className="text-xl font-bold text-[#002855] mb-1">Sổ Địa Chỉ</h2>
                                <p className="text-sm text-gray-500">Quản lý địa chỉ nhận hàng của bạn</p>
                            </div>
                            {!showForm && (
                                <button onClick={() => { setFormData(emptyForm); setEditingId(null); setShowForm(true); }} className="bg-tnc-orange hover:bg-tnc-orange-hover text-white px-4 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-sm transition-colors">
                                    <Plus size={18}/> Thêm Mới
                                </button>
                            )}
                        </div>

                        {showForm && (
                            <form onSubmit={handleSubmit} className="bg-white/60 backdrop-blur-sm p-5 md:p-6 rounded-xl border border-orange-200/60 mb-8 shadow-sm">
                                <h3 className="font-bold text-[#002855] mb-4 flex items-center gap-2">
                                    {editingId ? <Edit size={18} className="text-tnc-orange"/> : <Plus size={18} className="text-tnc-orange"/>} 
                                    {editingId ? 'Cập nhật địa chỉ' : 'Thêm địa chỉ mới'}
                                </h3>

                                {/* 👉 NOTE NHẮC NHỞ KHÁCH HÀNG Ở ĐÂY */}
                                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm mb-5 flex gap-2.5 items-start shadow-sm">
                                    <Info size={18} className="shrink-0 mt-0.5" />
                                    <p className="leading-relaxed">
                                        <strong>Lưu ý quan trọng:</strong> Vui lòng điền chính xác <strong>Số điện thoại</strong> và <strong>Email</strong>. Nhân viên Thành Nam PC sẽ sử dụng thông tin này để gọi điện xác nhận và tự động gửi email thông báo hành trình đơn hàng cho bạn.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Họ tên người nhận *</label>
                                            <input type="text" required value={formData.receiverName} onChange={e=>setFormData({...formData, receiverName: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange bg-white"/>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Số điện thoại *</label>
                                            <input type="tel" required value={formData.receiverPhone} onChange={e=>setFormData({...formData, receiverPhone: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange bg-white"/>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Email <span className="text-tnc-orange font-normal italic">(Khuyên dùng)</span></label>
                                            <input type="email" value={formData.receiverEmail} onChange={e=>setFormData({...formData, receiverEmail: e.target.value})} placeholder="Để nhận thông báo đơn hàng..." className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange bg-white"/>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Tỉnh / Thành phố *</label>
                                            <input type="text" placeholder="Kèm Quận/Huyện" required value={formData.province} onChange={e=>setFormData({...formData, province: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange bg-white"/>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Phường / Xã *</label>
                                            <input type="text" required value={formData.ward} onChange={e=>setFormData({...formData, ward: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange bg-white"/>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Địa chỉ cụ thể *</label>
                                        <input type="text" placeholder="Số nhà, ngõ ngách, tên đường..." required value={formData.specificAddress} onChange={e=>setFormData({...formData, specificAddress: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg outline-none focus:border-tnc-orange bg-white"/>
                                    </div>
                                </div>

                                <div className="flex flex-col md:flex-row md:justify-between md:items-center mt-5 pt-4 border-t border-orange-100 gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input type="checkbox" checked={formData.isDefault} onChange={e=>setFormData({...formData, isDefault: e.target.checked})} className="w-4 h-4 text-tnc-orange rounded focus:ring-tnc-orange cursor-pointer" />
                                        <span className="text-sm font-bold text-gray-700 group-hover:text-tnc-orange transition-colors">Đặt làm địa chỉ mặc định</span>
                                    </label>
                                    <div className="flex gap-3">
                                        <button type="button" onClick={handleCancel} className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 transition-colors text-gray-700 rounded-lg font-bold">Hủy bỏ</button>
                                        <button type="submit" className="px-8 py-2.5 bg-tnc-orange hover:bg-tnc-orange-hover transition-colors text-white rounded-lg font-bold shadow-sm">
                                            {editingId ? 'Cập Nhật' : 'Lưu Địa Chỉ'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}

                        {loading ? <div className="text-center py-10 font-medium text-gray-500">Đang tải dữ liệu...</div> : addresses.length === 0 ? (
                            <div className="text-center py-16 text-gray-500 flex flex-col items-center bg-white/60 rounded-xl border border-dashed border-orange-200/60">
                                <MapPin size={48} className="mb-3 text-gray-300"/> 
                                <p className="font-medium text-lg">Bạn chưa lưu địa chỉ nào.</p>
                                <p className="text-sm mt-1">Thêm địa chỉ ngay để thanh toán nhanh hơn!</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {paginatedAddresses.map(addr => (
                                    <div key={addr.addressId} className={`p-5 border-2 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center transition-all duration-300 gap-4 hover:-translate-y-1 ${addr.isDefault ? 'border-tnc-orange bg-orange-50/50 shadow-md' : 'border-orange-200/60 bg-white hover:border-tnc-orange hover:shadow-md'}`}>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                <span className="font-bold text-gray-800 text-lg border-r-2 border-orange-200/60 pr-3">{addr.receiverName}</span>
                                                <span className="text-gray-600 font-medium">{addr.receiverPhone}</span>
                                                {addr.isDefault && <span className="bg-tnc-orange text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm">MẶC ĐỊNH</span>}
                                            </div>
                                            {addr.receiverEmail && (
                                                <p className="text-gray-600 text-sm mb-1 flex items-center gap-1">
                                                    <Mail size={14} className="text-gray-400"/> {addr.receiverEmail}
                                                </p>
                                            )}
                                            <p className="text-gray-600 text-sm mb-1"><span className="font-medium text-gray-500 mr-1">Địa chỉ:</span> {addr.specificAddress}</p>
                                            <p className="text-gray-600 text-sm"><span className="font-medium text-gray-500 mr-1">Khu vực:</span> {addr.ward ? `${addr.ward}, ` : ''}{addr.province}</p>
                                        </div>

                                        <div className="flex items-center gap-2 w-full md:w-auto justify-end border-t border-orange-100 md:border-0 pt-3 md:pt-0">
                                            <button onClick={() => handleEditClick(addr)} className="text-blue-600 hover:text-blue-800 px-3 py-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors font-bold text-sm flex items-center gap-1">
                                                <Edit size={16}/> Sửa
                                            </button>
                                            <button onClick={() => handleDelete(addr.addressId)} className="text-red-500 hover:text-red-700 px-3 py-2 bg-red-50 rounded-lg hover:bg-red-100 transition-colors font-bold text-sm flex items-center gap-1">
                                                <Trash2 size={16}/> Xóa
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {totalPages > 1 && (
                                    <div className="border-t border-orange-200/60 pt-4 mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                                        <div className="text-xs text-gray-500 font-bold">
                                            Hiển thị <span className="text-gray-900">{(validCurrentPage - 1) * itemsPerPage + 1} - {Math.min(validCurrentPage * itemsPerPage, addresses.length)}</span> trên <span className="text-gray-900">{addresses.length}</span> địa chỉ
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button 
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={validCurrentPage === 1}
                                                className="p-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            
                                            <div className="flex items-center px-3 text-sm font-bold text-gray-700">
                                                Trang {validCurrentPage} / {totalPages}
                                            </div>

                                            <button 
                                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                                disabled={validCurrentPage === totalPages}
                                                className="p-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-tnc-orange hover:border-tnc-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default UserAddressPage;